import { ref, computed, watch } from 'vue';
import { getJobProfileVersions, getJobProfileRevision } from '@/api/jobProfile';
import { getHTMLDiff } from '@/helpers/diff';
import { formatJobLevel } from '@/helpers/jobLevelDisplay';

const joinBlocksHtml = (blocks) => (blocks || []).map((block) => block.html).join('');

export const profileSubtitle = (profile) => {
  if (!profile) return '';

  const subtitleParts = [profile.location, profile.job_code].filter(Boolean);

  return subtitleParts.join(subtitleParts.length > 1 ? ' - ' : '') || '—';
};

const buildSplitDiffFields = (oldVersion, newVersion) => {
  if (!oldVersion || !newVersion) return { oldDiffFields: [], newDiffFields: [] };

  const oldFields = oldVersion.fields || [];
  const newFields = newVersion.fields || [];
  const maxFieldLength = Math.max(oldFields.length, newFields.length);

  const oldDiffFieldsResult = [];
  const newDiffFieldsResult = [];

  for (let fieldIndex = 0; fieldIndex < maxFieldLength; fieldIndex++) {
    const oldFieldRow = oldFields[fieldIndex] || { blocks: [], header: [], footer: [] };
    const newFieldRow = newFields[fieldIndex] || { blocks: [], header: [], footer: [] };

    const baseField = {
      type: oldFieldRow.type || newFieldRow.type || `field-${fieldIndex}`,
      label: oldFieldRow.label || newFieldRow.label,
    };

    const { previous: oldHtml, current: newHtml } = getHTMLDiff(
      joinBlocksHtml(newFieldRow.blocks),
      joinBlocksHtml(oldFieldRow.blocks),
    );
    const { previous: oldHeaderHtml, current: newHeaderHtml } = getHTMLDiff(
      joinBlocksHtml(newFieldRow.header),
      joinBlocksHtml(oldFieldRow.header),
    );
    const { previous: oldFooterHtml, current: newFooterHtml } = getHTMLDiff(
      joinBlocksHtml(newFieldRow.footer),
      joinBlocksHtml(oldFieldRow.footer),
    );

    oldDiffFieldsResult.push({
      ...oldFieldRow,
      ...baseField,
      html: oldHtml,
      headerHtml: oldHeaderHtml,
      footerHtml: oldFooterHtml,
    });

    newDiffFieldsResult.push({
      ...newFieldRow,
      ...baseField,
      html: newHtml,
      headerHtml: newHeaderHtml,
      footerHtml: newFooterHtml,
    });
  }

  return { oldDiffFields: oldDiffFieldsResult, newDiffFields: newDiffFieldsResult };
};

const fetchSideData = async (side) => {
  if (!side?.profileUlid && !side?.revisionUlid) return { profile: null, versions: [] };

  if (side.isHrisProfile) {
    if (!side.profileUlid) return { profile: null, versions: [] };
    const { versions = [] } = await getJobProfileVersions({ ulid: side.profileUlid });
    const profile = side.revisionUlid
      ? (versions.find((v) => v.ulid === side.revisionUlid) || null)
      : (versions[0] || null);

    return { profile, versions };
  }

  if (!side.revisionUlid) return { profile: null, versions: [] };
  const { profile = null } = await getJobProfileRevision({ ulid: side.profileUlid, revisionUlid: side.revisionUlid });

  return { profile, versions: [] };
};

export const useJobProfileCompare = ({
  active,
  oldSide,
  newSide,
}) => {
  const loading = ref(false);
  const loadError = ref(null);
  const oldProfile = ref(null);
  const newProfile = ref(null);
  const oldVersions = ref([]);
  const newVersions = ref([]);

  const load = async () => {
    loadError.value = null;
    oldProfile.value = null;
    newProfile.value = null;
    oldVersions.value = [];
    newVersions.value = [];

    const oldValue = oldSide.value;
    const newValue = newSide.value;

    const sideIsLoadable = (side) => (
      side?.isHrisProfile ? !!side.profileUlid : !!side?.revisionUlid
    );

    if (!sideIsLoadable(oldValue) || !sideIsLoadable(newValue)) {
      loadError.value = 'Missing job profile version information.';

      return;
    }

    loading.value = true;

    try {
      // Optimisation: single fetch when both sides are from the same HRIS profile
      if (oldValue.isHrisProfile && newValue.isHrisProfile && oldValue.profileUlid === newValue.profileUlid) {
        const { versions = [] } = await getJobProfileVersions({ ulid: oldValue.profileUlid });

        oldVersions.value = versions;
        newVersions.value = versions;
        oldProfile.value = oldValue.revisionUlid
          ? (versions.find((v) => v.ulid === oldValue.revisionUlid) || null)
          : (versions[0] || null);
        newProfile.value = newValue.revisionUlid
          ? (versions.find((v) => v.ulid === newValue.revisionUlid) || null)
          : (versions[0] || null);
      } else {
        const [oldData, newData] = await Promise.all([
          fetchSideData(oldValue),
          fetchSideData(newValue),
        ]);

        oldProfile.value = oldData.profile;
        newProfile.value = newData.profile;
        oldVersions.value = oldData.versions;
        newVersions.value = newData.versions;
      }

      if (!oldProfile.value || !newProfile.value) {
        loadError.value = 'Could not load one or both job profile versions.';
      }
    } catch (error) {
      loadError.value = error?.message || 'Failed to load job profile versions.';
    } finally {
      loading.value = false;
    }
  };

  watch(
    () => [active.value, oldSide.value, newSide.value],
    () => {
      if (active.value) {
        load();
      }
    },
    { immediate: true, deep: true },
  );

  const titleDiff = computed(() => {
    if (!oldProfile.value || !newProfile.value) return { previous: '', current: '' };

    return getHTMLDiff(
      newProfile.value.title || '',
      oldProfile.value.title || '',
    );
  });

  const oldTitleDiffHtml = computed(() => titleDiff.value.previous);
  const newTitleDiffHtml = computed(() => titleDiff.value.current);

  const splitDiffFields = computed(() => buildSplitDiffFields(oldProfile.value, newProfile.value));
  const oldDiffFields = computed(() => splitDiffFields.value.oldDiffFields);
  const newDiffFields = computed(() => splitDiffFields.value.newDiffFields);

  const oldVersionIsCurrentVersion = computed(() => {
    const vers = oldVersions.value;

    if (!vers?.length || !oldProfile.value) return false;

    return vers[0]?.ulid === oldProfile.value.ulid;
  });

  const newVersionIsCurrentVersion = computed(() => {
    const vers = newVersions.value;

    if (!vers?.length || !newProfile.value) return false;

    return vers[0]?.ulid === newProfile.value.ulid;
  });

  const oldProfileSubtitle = computed(() => profileSubtitle(oldProfile.value));
  const newProfileSubtitle = computed(() => profileSubtitle(newProfile.value));

  const metaDiff = computed(() => {
    if (!oldProfile.value || !newProfile.value) return { previous: '', current: '' };

    const metaFields = [
      { old: oldProfile.value.location || '', new: newProfile.value.location || '' },
      { old: oldProfile.value.job_code || '', new: newProfile.value.job_code || '' },
      {
        old: formatJobLevel(oldProfile.value.job_level, { mode: 'long' }),
        new: formatJobLevel(newProfile.value.job_level, { mode: 'long' }),
      },
    ].filter(({ old, new: n }) => old || n);

    const oldParts = [];
    const newParts = [];

    metaFields.forEach(({ old, new: n }) => {
      const { previous, current } = getHTMLDiff(n, old);

      oldParts.push(previous);
      newParts.push(current);
    });

    return {
      previous: oldParts.join('<span class="meta-separator"> — </span>'),
      current: newParts.join('<span class="meta-separator"> — </span>'),
    };
  });

  const oldMetaDiffHtml = computed(() => metaDiff.value.previous);
  const newMetaDiffHtml = computed(() => metaDiff.value.current);

  return {
    loading,
    loadError,
    oldProfile,
    newProfile,
    oldDiffFields,
    newDiffFields,
    oldTitleDiffHtml,
    newTitleDiffHtml,
    oldMetaDiffHtml,
    newMetaDiffHtml,
    oldVersionIsCurrentVersion,
    newVersionIsCurrentVersion,
    oldProfileSubtitle,
    newProfileSubtitle,
  };
};
